import type {
  ChargeCodeResponse,
  ChargeCodeSuggestionResponse,
  UserResponse,
} from '@contract-analyzer/api-client';

import type { AuthUser } from '@/hooks/auth-context';

const CHARGE_CODES_VIEW_PERMISSION = 'charge_codes.view';
const CHARGE_CODES_SUBMIT_PERMISSION = 'charge_codes.submit';
const CHARGE_CODES_MANAGE_PERMISSION = 'charge_codes.manage';
const TENANT_SETTINGS_PERMISSION = 'tenant.settings';

/** Shown wherever a charge code has no description, so every surface says it the same way. */
export const NO_CHARGE_CODE_DESCRIPTION = 'No description.';

type ChargeCodeUserLookup = Pick<AuthUser, 'display_name' | 'permissions' | 'is_superadmin' | 'user_id'>;

const CHARGE_CODE_STATUS_LABELS: Readonly<Record<ChargeCodeResponse['status'], string>> = {
  active: 'Active',
  approved: 'Approved',
  pending_approval: 'Pending approval',
  rejected: 'Rejected',
  disabled: 'Disabled',
};

type ChargeCodeSuggestionSource = ChargeCodeSuggestionResponse['sources'][number];

/** Why a code is suggested, in the reader's terms rather than the scorer's. */
const CHARGE_CODE_SUGGESTION_SOURCE_LABELS: Readonly<Record<ChargeCodeSuggestionSource, string>> = {
  workspace_history: 'Recent projects',
  user_history: 'Submitted by you',
  tenant_catalog: 'Workspace catalog',
};

export function formatChargeCodeStatus(status: ChargeCodeResponse['status']): string {
  return CHARGE_CODE_STATUS_LABELS[status];
}

export function formatChargeCodeSuggestionSource(source: ChargeCodeSuggestionSource): string {
  return CHARGE_CODE_SUGGESTION_SOURCE_LABELS[source];
}

export function getChargeCodeBadgeVariant(
  status: ChargeCodeResponse['status'],
): 'default' | 'outline' | 'secondary' {
  if (status === 'pending_approval') return 'secondary';
  if (status === 'disabled' || status === 'rejected') return 'outline';
  return 'default';
}

function isUsableChargeCodeStatus(status: ChargeCodeResponse['status']): boolean {
  return status === 'active' || status === 'approved';
}

export function getDefaultTenantChargeCode(
  chargeCodes: readonly ChargeCodeResponse[],
): ChargeCodeResponse | null {
  const usableChargeCodes = chargeCodes.filter((chargeCode) =>
    isUsableChargeCodeStatus(chargeCode.status),
  );

  if (usableChargeCodes.length === 0) {
    return null;
  }

  return [...usableChargeCodes].sort(
    (left, right) => Date.parse(left.created_at) - Date.parse(right.created_at),
  )[0] ?? null;
}

export function getUserDisplayName(
  users: readonly UserResponse[],
  userId: string | null | undefined,
  currentUser?: ChargeCodeUserLookup | null,
): string {
  if (!userId) return '—';
  if (currentUser?.user_id === userId) return currentUser.display_name;
  return users.find((user) => user.user_id === userId)?.display_name ?? userId;
}

export function canViewChargeCodes(user: ChargeCodeUserLookup | null | undefined): boolean {
  return user?.is_superadmin === true || user?.permissions.includes(CHARGE_CODES_VIEW_PERMISSION) === true;
}

export function canSubmitChargeCodes(user: ChargeCodeUserLookup | null | undefined): boolean {
  return user?.is_superadmin === true || user?.permissions.includes(CHARGE_CODES_SUBMIT_PERMISSION) === true;
}

export function canManageChargeCodes(user: ChargeCodeUserLookup | null | undefined): boolean {
  return user?.is_superadmin === true || user?.permissions.includes(CHARGE_CODES_MANAGE_PERMISSION) === true;
}

export function canViewChargeCodeCatalog(user: ChargeCodeUserLookup | null | undefined): boolean {
  return user?.is_superadmin === true || user?.permissions.includes(TENANT_SETTINGS_PERMISSION) === true;
}

export function canAccessWorkspaceBilling(user: AuthUser | null | undefined): boolean {
  return user?.tenant_id != null && (
    canViewChargeCodeCatalog(user) || canViewChargeCodes(user) || canSubmitChargeCodes(user)
  );
}

export function sortChargeCodesByCreatedAt(
  chargeCodes: readonly ChargeCodeResponse[],
): ChargeCodeResponse[] {
  return [...chargeCodes].sort((left, right) =>
    Date.parse(right.created_at) - Date.parse(left.created_at),
  );
}
